// ========================================
// UNIFIED ORDER HISTORY V3
// Universal order history component for all order types
// Single component that displays material, heat_treatment, and coating orders
// ========================================

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  Send as SendIcon,
  LocalShipping as DeliveryIcon,
  FileUpload as UploadIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import { unifiedOrderService, UnifiedOrderService } from '../../services/unifiedOrderService';
import {
  OrderHeader,
  UnifiedOrder,
  DeliveryRequest,
  SendOrderRequest
} from '../../types/unifiedOrderTypes';
import OrderDetailModal from './OrderDetailModal';
import { formatDate } from '../../utils/dateUtils';

const UnifiedOrderHistory: React.FC = () => {
  const { showNotification } = useNotification();

  // Data state
  const [orders, setOrders] = useState<OrderHeader[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<UnifiedOrder | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Pagination state
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Filter state
  const [filters, setFilters] = useState({
    order_type: '',
    status: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Dialog states
  const [orderDetailModalOpen, setOrderDetailModalOpen] = useState<boolean>(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [sendDialogOpen, setSendDialogOpen] = useState<boolean>(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);
  const [pdfLanguageDialogOpen, setPdfLanguageDialogOpen] = useState<boolean>(false);

  // Form states for dialogs
  const [sendEmails, setSendEmails] = useState<string>('');
  const [sendMessage, setSendMessage] = useState<string>('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<'delivery_note' | 'invoice' | 'extra_file'>('delivery_note');
  const [selectedPdfLanguage, setSelectedPdfLanguage] = useState<'de' | 'en'>('de');
  const [orderToFinalize, setOrderToFinalize] = useState<string | null>(null);

  // Load orders on component mount and when filters change
  useEffect(() => {
    loadOrders();
  }, [page, rowsPerPage, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // ========================================
  // DATA LOADING
  // ========================================

  const loadOrders = async () => {
    try {
      setLoading(true);
      
      const filterParams: any = {
        ...filters,
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };

      // Remove empty filters and ensure proper typing
      Object.keys(filterParams).forEach(key => {
        if (!filterParams[key]) {
          delete filterParams[key];
        }
      });

      // Ensure order_type is properly typed
      if (filterParams.order_type && !['material', 'heat_treatment', 'coating'].includes(filterParams.order_type)) {
        delete filterParams.order_type;
      }

      const response = await unifiedOrderService.getOrders(filterParams);
      
      if (response.success && response.data) {
        setOrders(response.data);
        // Note: In a real implementation, the API should return total count
        setTotalCount(response.data.length);
      }
    } catch (error: any) {
      showNotification('Fehler beim Laden der Bestellungen: ' + error.message, 'error'); // Error loading orders: ${error.message}
    } finally {
      setLoading(false);
    }
  };

  const refreshOrders = async () => {
    try {
      setRefreshing(true);
      await loadOrders();
      showNotification('Bestellungen erfolgreich aktualisiert', 'success'); // Orders refreshed successfully
    } catch (error: any) {
      showNotification('Fehler beim Aktualisieren der Bestellungen: ' + error.message, 'error'); // Error refreshing orders: ${error.message}
    } finally {
      setRefreshing(false);
    }
  };

  const loadOrderDetails = async (orderId: string) => {
    setSelectedOrderId(orderId);
    setOrderDetailModalOpen(true);
  };

  // ========================================
  // ORDER ACTIONS
  // ========================================

  const handleFinalizeOrder = async (orderId: string, language: 'de' | 'en' = 'de') => {
    try {
      const response = await unifiedOrderService.finalizeOrder(orderId, language);
      if (response.success) {
        showNotification(`Bestellung finalisiert und PDF generiert! (${language.toUpperCase()})`, 'success'); // Order finalized and PDF generated! (LANGUAGE)
        await loadOrders();
      }
    } catch (error: any) {
      showNotification('Fehler beim Finalisieren der Bestellung: ' + error.message, 'error'); // Error finalizing order: ${error.message}
    }
  };

  const handlePdfLanguageSelection = () => {
    if (!orderToFinalize) return;
    
    handleFinalizeOrder(orderToFinalize, selectedPdfLanguage);
    setPdfLanguageDialogOpen(false);
    setOrderToFinalize(null);
  };

  const handleSendOrder = async () => {
    if (!selectedOrder) return;

    try {
      const emails = sendEmails.split(',').map(email => email.trim()).filter(email => email);
      if (emails.length === 0) {
        showNotification('Bitte geben Sie mindestens eine E-Mail-Adresse ein', 'error'); // Please enter at least one email address
        return;
      }

      const sendData: SendOrderRequest = {
        emails,
        message: sendMessage || undefined
      };

      const response = await unifiedOrderService.sendOrder(selectedOrder.id, sendData);
      if (response.success) {
        showNotification('Bestellung erfolgreich gesendet!', 'success'); // Order sent successfully!
        setSendDialogOpen(false);
        setSendEmails('');
        setSendMessage('');
        await loadOrders();
      }
    } catch (error: any) {
      showNotification('Fehler beim Senden der Bestellung: ' + error.message, 'error'); // Error sending order: ${error.message}
    }
  };

  const handleFileUpload = async () => {
    if (!selectedOrder || !uploadFile) return;

    try {
      let response;
      switch (uploadType) {
        case 'delivery_note':
          response = await unifiedOrderService.uploadDeliveryNote(selectedOrder.id, uploadFile);
          break;
        case 'invoice':
          response = await unifiedOrderService.uploadInvoice(selectedOrder.id, uploadFile);
          break;
        case 'extra_file':
          response = await unifiedOrderService.uploadExtraFile(selectedOrder.id, uploadFile);
          break;
      }

      if (response.success) {
        showNotification('Datei erfolgreich hochgeladen!', 'success'); // File uploaded successfully!
        setUploadDialogOpen(false);
        setUploadFile(null);
        await loadOrders();
      }
    } catch (error: any) {
      showNotification('Fehler beim Hochladen der Datei: ' + error.message, 'error'); // Error uploading file: ${error.message}
    }
  };

  // ========================================
  // UI HELPERS
  // ========================================

  const getStatusChip = (status: string) => {
    const color = UnifiedOrderService.getStatusColor(status);
    const displayName = UnifiedOrderService.getStatusDisplayName(status);
    
    return (
      <Chip
        label={displayName}
        size="small"
        sx={{
          backgroundColor: color,
          color: 'white',
          fontWeight: 'bold'
        }}
      />
    );
  };

  const getOrderTypeChip = (orderType: string) => {
    const colors = {
      material: 'primary',
      heat_treatment: 'secondary',
      coating: 'info'
    };
    
    return (
      <Chip
        label={UnifiedOrderService.getOrderTypeDisplayName(orderType)}
        color={colors[orderType as keyof typeof colors] as any}
        size="small"
      />
    );
  };

  const formatDateLocal = (dateString: string) => {
    return formatDate(new Date(dateString));
  };

  // ========================================
  // RENDER
  // ========================================

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Bestellhistorie - Alle Typen {/* Order History - All Types */}
        </Typography>
        <Box>
          <Button
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Filter {/* Filters */}
          </Button>
          <Button
            startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={refreshOrders}
            disabled={refreshing}
            variant="contained"
          >
            Aktualisieren {/* Refresh */}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filter {/* Filters */}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Bestellungstyp {/* Order Type */}</InputLabel>
                  <Select
                    value={filters.order_type}
                    onChange={(e) => setFilters({ ...filters, order_type: e.target.value })}
                    label="Bestellungstyp" // Order Type
                  >
                    <MenuItem value="">Alle Typen {/* All Types */}</MenuItem>
                    <MenuItem value="material">Materialbestellungen {/* Material Orders */}</MenuItem>
                    <MenuItem value="heat_treatment">Wärmebehandlung {/* Heat Treatment */}</MenuItem>
                    <MenuItem value="coating">Beschichtungsaufträge {/* Coating Orders */}</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="">Alle Status {/* All Statuses */}</MenuItem>
                    <MenuItem value="draft">Entwurf {/* Draft */}</MenuItem>
                    <MenuItem value="ready">Bereit {/* Ready */}</MenuItem>
                    <MenuItem value="sent">Gesendet {/* Sent */}</MenuItem>
                    <MenuItem value="partial">Teilweise geliefert {/* Partially Delivered */}</MenuItem>
                    <MenuItem value="delivered">Geliefert {/* Delivered */}</MenuItem>
                    <MenuItem value="completed">Abgeschlossen {/* Completed */}</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  sx={{ flex: 2 }}
                  label="Suchen" // Search
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Nach Bestell-ID, Lieferant oder Notizen suchen..." // Search by order ID, supplier, or notes...
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Orders Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Bestell-ID {/* Order ID */}</TableCell>
                <TableCell>Typ {/* Type */}</TableCell>
                <TableCell>Lieferant {/* Supplier */}</TableCell>
                <TableCell>Datum {/* Date */}</TableCell>
                <TableCell>Liefertermin {/* Due Date */}</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Positionen {/* Positions */}</TableCell>
                <TableCell>Aktionen {/* Actions */}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">
                      Keine Bestellungen gefunden {/* No orders found */}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {order.id.slice(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getOrderTypeChip(order.order_type)}
                    </TableCell>
                    <TableCell>
                      {order.supplier?.name || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      {formatDateLocal(order.order_date)}
                    </TableCell>
                    <TableCell>
                      {order.due_date ? formatDateLocal(order.due_date) : '-'}
                    </TableCell>
                    <TableCell>
                      {getStatusChip(order.status)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {order.total_positions} pos, {order.total_quantity} qty
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Details anzeigen" /* View Details */>
                          <IconButton
                            size="small"
                            onClick={() => loadOrderDetails(order.id)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        
                        {order.status === 'draft' && (
                          <Tooltip title="PDF generieren" /* Generate PDF */>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setOrderToFinalize(order.id);
                                setPdfLanguageDialogOpen(true);
                              }}
                              color="primary"
                            >
                              <PdfIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {order.status === 'ready' && (
                          <Tooltip title="Bestellung senden" /* Send Order */>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedOrder({ ...order, positions: [] });
                                // Pre-populate supplier email if available
                                if (order.supplier?.email) {
                                  setSendEmails(order.supplier.email);
                                } else {
                                  setSendEmails('');
                                }
                                setSendDialogOpen(true);
                              }}
                              color="secondary"
                            >
                              <SendIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {['sent', 'partial'].includes(order.status) && (
                          <Tooltip title="Details anzeigen & Lieferung verwalten" /* View Details & Manage Delivery */>
                            <IconButton
                              size="small"
                              onClick={() => loadOrderDetails(order.id)}
                              sx={{ color: '#2e7d32' }}
                            >
                              <DeliveryIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="Dateien hochladen" /* Upload Files */>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedOrder({ ...order, positions: [] });
                              setUploadDialogOpen(true);
                            }}
                          >
                            <UploadIcon />
                          </IconButton>
                        </Tooltip>
                        
                        {order.pdf_path && (
                          <Tooltip title="PDF herunterladen" /* Download PDF */>
                            <IconButton
                              size="small"
                              component="a"
                              href={unifiedOrderService.getPdfUrl(order.id)}
                              target="_blank"
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      {/* Order Detail Modal */}
      <OrderDetailModal
        open={orderDetailModalOpen}
        onClose={() => {
          setOrderDetailModalOpen(false);
          setSelectedOrderId(null);
        }}
        orderId={selectedOrderId}
        onOrderUpdated={loadOrders}
      />

      {/* Send Order Dialog */}
      <Dialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bestellung senden</DialogTitle> {/* Send Order */}
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* Supplier Information */}
            {selectedOrder?.supplier && (
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Senden an Lieferant: {/* Sending to Supplier: */}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedOrder.supplier.name}
                </Typography>
                {selectedOrder.supplier.email && (
                  <Typography variant="body2" color="text.secondary">
                    Standard-E-Mail: {selectedOrder.supplier.email} {/* Default Email: {selectedOrder.supplier.email} */}
                  </Typography>
                )}
              </Box>
            )}
            
            <TextField
              fullWidth
              label="E-Mail-Adressen" // Email Addresses
              value={sendEmails}
              onChange={(e) => setSendEmails(e.target.value)}
              placeholder="email1@example.com, email2@example.com"
              helperText={selectedOrder?.supplier?.email 
                ? "Lieferanten-E-Mail vorausgefüllt. Fügen Sie weitere E-Mails durch Kommas getrennt hinzu, falls nötig." // Supplier email pre-filled. Add additional emails separated by commas if needed.
                : "Mehrere E-Mails durch Kommas trennen" // Separate multiple emails with commas
              }
              sx={{ mb: 3 }}
            />
            <TextField
              fullWidth
              label="Nachricht" // Message
              multiline
              rows={3}
              value={sendMessage}
              onChange={(e) => setSendMessage(e.target.value)}
              placeholder="Optionale Nachricht, die mit der Bestellung eingefügt werden soll..." // Optional message to include with the order...
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSendDialogOpen(false);
            setSendEmails('');
            setSendMessage('');
          }}>Abbrechen</Button> {/* Cancel */}
          <Button onClick={handleSendOrder} variant="contained">
            Bestellung senden {/* Send Order */}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload File Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Datei hochladen</DialogTitle> {/* Upload File */}
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Dateityp</InputLabel> {/* File Type */}
              <Select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value as any)}
                label="Dateityp" // File Type
              >
                <MenuItem value="delivery_note">Lieferschein</MenuItem> {/* Delivery Note */}
                <MenuItem value="invoice">Rechnung</MenuItem> {/* Invoice */}
                <MenuItem value="extra_file">Extra Dokument</MenuItem> {/* Extra File */}
              </Select>
            </FormControl>
            
            <input
              type="file"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              style={{ width: '100%' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Abbrechen</Button> {/* Cancel */}
          <Button 
            onClick={handleFileUpload} 
            variant="contained"
            disabled={!uploadFile}
          >
            Datei hochladen {/* Upload File */}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Language Selection Dialog */}
      <Dialog
        open={pdfLanguageDialogOpen}
        onClose={() => {
          setPdfLanguageDialogOpen(false);
          setOrderToFinalize(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PdfIcon color="primary" />
            PDF Sprache wählen {/* Choose PDF Language */}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Wählen Sie die Sprache für das zu generierende PDF:
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>PDF Sprache</InputLabel>
              <Select
                value={selectedPdfLanguage}
                onChange={(e) => setSelectedPdfLanguage(e.target.value as 'de' | 'en')}
                label="PDF Sprache"
              >
                <MenuItem value="de">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    🇩🇪 Deutsch
                  </Box>
                </MenuItem>
                <MenuItem value="en">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    🇬🇧 English
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPdfLanguageDialogOpen(false);
            setOrderToFinalize(null);
          }}>
            Abbrechen {/* Cancel */}
          </Button>
          <Button 
            onClick={handlePdfLanguageSelection}
            variant="contained"
            startIcon={<PdfIcon />}
            color="primary"
          >
            PDF generieren {/* Generate PDF */}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnifiedOrderHistory;
